import Dexie, { Table } from 'dexie';
import { SurveyType } from '@/types/surveys.types';
import type { OfflineSurveyStatus } from '@/constants/offline.constants';

// To see this database
// Go to DevTools, Application table, select IndexedDB, within it QluuClient, expland the QluuClient to view tables

/**
 * Offline route survey submission
 */
export interface OfflineRouteSurvey {
  id?: number;
  localId: string;
  // Survey data in the format expected by the API (CreateRouteSurveyRequest)
  categories: Array<{
    id: number;
    questions: Array<{
      id: number;
      answer: {
        optionId: number;
        score: number;
        comment?: string;
      };
    }>;
  }>;
  enpsScore: number; // 0-10 scale (required)
  enpsComments?: string;
  // Metadata
  status: OfflineSurveyStatus;
  createdAt: number;
  syncedAt?: number;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Offline incident report submission
 */
export interface OfflineIncidentReport {
  id?: number;
  localId: string;
  description: string;
  images: File[]; // Store actual File objects
  status: OfflineSurveyStatus;
  createdAt: number;
  syncedAt?: number;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Offline customer feedback submission
 */
export interface OfflineCustomerFeedback {
  id?: number;
  localId: string;
  customerName: string;
  customerFeedback: string;
  status: OfflineSurveyStatus;
  createdAt: number;
  syncedAt?: number;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Cached survey configuration
 */
export interface CachedSurveyConfig {
  id: number;
  data: {
    categories: string[];
    routeSurveyConfig: {
      categories: Array<{
        id: number;
        name: string;
        questions: Array<{
          id: number;
          text: string;
          options: Array<{
            id: number;
            label: string;
            score: number;
          }>;
        }>;
      }>;
    };
  };
  cachedAt: number;
  expiresAt: number;
}

/**
 * Cached surveys for offline viewing
 */
export interface CachedSurvey {
  id?: number;
  uuid: string;
  accountName: string;
  accountUuid: string;
  type: SurveyType;
  date: string;
  driverName: string;
  driverUuid: string;
  managerName?: string;
  alertType: string;
  overallScore: number | null;
  enpsScore: number | null;
  commentsCount: number;
  viewed: boolean;
  categoryScore: number | null;
  comments?: { label: string; text: string }[];
  cachedAt: number;
}

/**
 * Cached surveys pagination metadata
 */
export interface CachedSurveysPagination {
  id: number; // Always 1, single record
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
  cachedAt: number;
  expiresAt: number;
}

/**
 * IndexedDB database for offline storage
 */
export class ClientDatabase extends Dexie {
  offlineRouteSurveys!: Table<OfflineRouteSurvey, number>;
  offlineIncidentReports!: Table<OfflineIncidentReport, number>;
  offlineCustomerFeedbacks!: Table<OfflineCustomerFeedback, number>;
  cachedSurveyConfig!: Table<CachedSurveyConfig, number>;
  cachedSurveys!: Table<CachedSurvey, number>;
  cachedSurveysPagination!: Table<CachedSurveysPagination, number>;

  constructor() {
    super('QluuClient');

    this.version(1).stores({
      offlineRouteSurveys: '++id, localId, status, createdAt',
      offlineIncidentFeedbacks: '++id, localId, status, createdAt',
      cachedSurveyConfig: 'id, expiresAt',
    });

    // Version 2: Add cached surveys tables
    this.version(2).stores({
      offlineRouteSurveys: '++id, localId, status, createdAt',
      offlineIncidentFeedbacks: '++id, localId, status, createdAt',
      cachedSurveyConfig: 'id, expiresAt',
      cachedSurveys: '++id, uuid, cachedAt',
      cachedSurveysPagination: 'id, expiresAt',
    });

    // Version 3: Add offline customer feedbacks table
    this.version(3).stores({
      offlineRouteSurveys: '++id, localId, status, createdAt',
      offlineIncidentFeedbacks: '++id, localId, status, createdAt',
      offlineCustomerFeedbacks: '++id, localId, status, createdAt',
      cachedSurveyConfig: 'id, expiresAt',
      cachedSurveys: '++id, uuid, cachedAt',
      cachedSurveysPagination: 'id, expiresAt',
    });

    // Version 4: Rename offlineIncidentFeedbacks → offlineIncidentReports
    this.version(4).stores({
      offlineRouteSurveys: '++id, localId, status, createdAt',
      offlineIncidentFeedbacks: null, // drop old table
      offlineIncidentReports: '++id, localId, status, createdAt',
      offlineCustomerFeedbacks: '++id, localId, status, createdAt',
      cachedSurveyConfig: 'id, expiresAt',
      cachedSurveys: '++id, uuid, cachedAt',
      cachedSurveysPagination: 'id, expiresAt',
    });
  }
}

// Export singleton instance
export const db = new ClientDatabase();
